import React from 'react';
import { AlertTriangle } from 'lucide-react';
import type { ScopeNotice } from '@/lib/tenantScope';

/**
 * SUB-3.12 · NFR8. The half of the AC the user actually experiences.
 *
 * ⚠️ IT BLOCKS. That is the requirement, not a design flourish. NFR8 says the
 * system "never silently returns a subset of what they believe they are looking
 * at", and a dismissible toast over a page whose data has already changed
 * underneath is exactly that subset with a decoration on top. Until the reload,
 * every figure on the page belongs to a different account than the one the
 * header names.
 *
 * ⚠️ IT IS RENDERED BY `TenantProvider` ITSELF, not mounted in a layout. A
 * surface that has to be remembered in each layout is a surface that will be
 * missing from one of them, and the one it is missing from will be the one
 * someone is looking at.
 */
export function ScopeChangeNotice({
  notice,
  onReload,
}: {
  notice: ScopeNotice | null;
  onReload: () => void;
}) {
  if (!notice) return null;

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="scope-change-title"
      aria-describedby="scope-change-body"
      // Above the slide panels (150-180) and the sidebar (200). This notice
      // means the content behind it is wrong, so nothing may sit over it.
      className="fixed inset-0 z-[300] flex items-center justify-center bg-black/60 p-4"
    >
      <div className="w-full max-w-md rounded-lg border border-border bg-background p-6 shadow-lg">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" aria-hidden="true" />
          <div className="min-w-0">
            <h2 id="scope-change-title" className="text-base font-semibold text-foreground">
              {notice.title}
            </h2>
            <p id="scope-change-body" className="mt-2 text-sm text-muted-foreground">
              {notice.body}
            </p>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            type="button"
            autoFocus
            onClick={onReload}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            {notice.actionLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ScopeChangeNotice;
